import {NgIf} from "@angular/common";
import {Component, Input, OnInit} from '@angular/core';
import {FlexModule} from "@angular/flex-layout";
import {MatButtonModule} from "@angular/material/button";
import {MatDialog} from "@angular/material/dialog";
import {MatIconModule} from "@angular/material/icon";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {FormQuestionFilter} from "@core/model/retailer/application-form.model";
import {
  AddDocumentDialogComponent
} from "@features/leasing/lease-view/lease-documents/add-document-dialog/add-document-dialog.component";
import {LeaseDocumentsService} from "@features/leasing/lease-view/lease-documents/lease-documents.service";
import {LeaseDocument, LeaseStatus, LeaseSummary} from "@features/leasing/leasing.model";
import {LeasingService} from "@features/leasing/leasing.service";
import {AppSharedModule} from "@modules/app-shared.module";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {DocumentFile} from "@ui-kit/webkit/components/image-carousel/image-carousel.model";
import {ImageGridComponent} from "@ui-kit/webkit/components/image-grid/image-grid.component";
import {ApplicationsService} from "../../../applications/applications.service";

@Component({
  selector: 'app-retailer-showcase',
  standalone: true,
  imports: [
    FlexModule,
    MatButtonModule,
    MatIconModule,
    AppSharedModule,
    MatProgressSpinnerModule,
    ImageGridComponent,
    NgIf
  ],
  templateUrl: './retailer-showcase.component.html'
})
export class RetailerShowcaseComponent implements OnInit {
  @Input() id!: string;
  @Input() companyName!: string;

  applicationImageDocs: FormQuestionFilter[] = [];
  applicationImageFiles: DocumentFile[] = [];

  private pageSize = 10;
  private currentLoadedIndex = 0;
  isLoading = false;
  hasMoreImagesToLoad = false;

  leaseImageMap = new Map<string, {
    lease: LeaseSummary,
    documents: LeaseDocument[],
    pageSize: number,
    isLoading: boolean,
    currentLoadedIndex: number,
    hasMoreImagesToLoad: boolean,
    files: DocumentFile[]
  }>();

  constructor(private applicationsService: ApplicationsService,
              private documentsService: LeaseDocumentsService,
              private leasingService: LeasingService,
              public utils: UtilsService,
              private matDialog: MatDialog,
              public dateUtils: DateUtilsService) {
  }

  ngOnInit() {
    this.loadApplicationImages();
    this.loadLeases();
  }

  loadApplicationImages() {
    this.applicationsService.getAssessmentImages(this.id).subscribe({
      next: value => {
        this.applicationImageDocs = value;
        this.loadInitialApplicationImages();
      },
      error: err => console.log(err)
    })
  }

  loadLeases() {
    const page = {size: '1000', page: '0', sort: 'leaseStartDate,desc'};
    const filter = [
      {name: 'retailerId', val: this.id},
      {name: 'statusList', val: [LeaseStatus.PLANNED, LeaseStatus.APPROVED, LeaseStatus.ACTIVE, LeaseStatus.EXPIRED].join(',')},
    ];
    this.leasingService.getAll('/lease-schedule', page, filter)
      .subscribe({
        next: (value: { content: LeaseSummary[] }) => {
          value.content.forEach(lease => this.leaseImageMap.set(lease.uuid!, {
            lease: lease,
            documents: [],
            pageSize: 10,
            isLoading: false,
            currentLoadedIndex: 0,
            hasMoreImagesToLoad: false,
            files: []
          }))
          this.loadLeaseDocuments();
        },
        error: err => {
          console.log(err);
        }
      });
  }

  loadLeaseDocuments() {
    this.leaseImageMap.forEach((mapValue, key) => {
      this.documentsService.getLeaseDocuments(key).subscribe({
        next: value => {
          mapValue.documents = value.filter(f => f.documentTypeCode == 'SHOWCASE_IMAGE')
            .sort((a, b) => new Date(a.createDate!).getTime() - new Date(b.createDate!).getTime());
          this.loadInitialLeaseImages(key);
        },
        error: err => console.log(err)
      })
    })
  }

  private loadInitialApplicationImages() {
    this.currentLoadedIndex = 0;
    this.applicationImageFiles = [];
    this.loadMoreApplicationImages();
  }

  private loadInitialLeaseImages(leaseId: string) {
    const lease = this.leaseImageMap.get(leaseId)!;
    lease.currentLoadedIndex = 0
    lease.files = [];
    this.loadMoreLeaseImages(leaseId);
  }

  loadMoreApplicationImages() {
    if (this.isLoading) return;

    this.isLoading = true;
    const endIndex = Math.min(this.currentLoadedIndex + this.pageSize, this.applicationImageDocs.length);
    const imagesToLoad = this.applicationImageDocs.slice(this.currentLoadedIndex, endIndex);
    let loadedCount = 0;

    imagesToLoad.forEach(docObject => {
      this.applicationsService.downloadFormFile(this.id, docObject.fileValue!.fileUuid!, {...docObject}).subscribe({
        next: (value) => {
          if (docObject.fileValue) {
            const type = ('application/' + docObject.fileValue.fileType).toString();
            const file = new Blob([value], {type});
            const fileURL = URL.createObjectURL(file);

            this.applicationImageFiles.push({
              fileUuid: docObject.fileValue.fileUuid,
              documentId: null,
              fileUrl: fileURL,
              blob: value,
              fileType: docObject.fileValue.fileType,
              fileName: docObject.fileValue.fileName,
            });
          }

          loadedCount++;
          if (loadedCount === imagesToLoad.length) {
            this.currentLoadedIndex = endIndex;
            this.hasMoreImagesToLoad = this.currentLoadedIndex < this.applicationImageDocs.length;
            this.isLoading = false;
          }
        },
        error: err => {
          console.log(err);
          loadedCount++;
          if (loadedCount === imagesToLoad.length) {
            this.isLoading = false;
          }
        }
      })
    })

    // If there are no images to load, reset loading state
    if (imagesToLoad.length === 0) {
      this.isLoading = false;
    }
  }

  loadMoreLeaseImages(leaseId: string) {
    const lease = this.leaseImageMap.get(leaseId)!;
    if (lease.isLoading) return;

    lease.isLoading = true;
    const endIndex = Math.min(this.currentLoadedIndex + this.pageSize, lease.documents.length);
    const imagesToLoad = lease.documents.slice(this.currentLoadedIndex, endIndex);
    let loadedCount = 0;

    imagesToLoad.forEach(docObject => {
      this.documentsService.getDocumentFile(leaseId, docObject.uuid!).subscribe({
        next: (value) => {
          const type = ('application/' + docObject.mimeType).toString();
          const file = new Blob([value], {type});
          const fileURL = URL.createObjectURL(file);
          lease.files.push({
            fileUuid: docObject.fileUuid,
            documentId: docObject.documentUuid!,
            fileUrl: fileURL,
            blob: value,
            fileType: docObject.mimeType,
            fileName: docObject.fileName
          });

          loadedCount++;
          if (loadedCount === imagesToLoad.length) {
            lease.currentLoadedIndex = endIndex;
            lease.hasMoreImagesToLoad = lease.currentLoadedIndex < lease.documents.length;
            lease.isLoading = false;
          }
        },
        error: err => {
          console.log(err);
          loadedCount++;
          if (loadedCount === imagesToLoad.length) {
            lease.isLoading = false;
          }
        }
      })
    })

    // If there are no images to load, reset loading state
    if (imagesToLoad.length === 0) {
      lease.isLoading = false;
    }
  }

  addImage(leaseId: string) {
    this.matDialog.open(AddDocumentDialogComponent, {
      panelClass: 'dialog-large',
      width: '250px',
      data: {
        leaseId: leaseId,
        setType: "SHOWCASE_IMAGE"
      }
    }).afterClosed()
      .subscribe(() => {
        this.loadLeaseDocuments();
      });
  }

  removeDocumentFromLease(docId: string, leaseId: string) {
    if (docId) {
      this.documentsService.removeLeaseDocument(docId, leaseId).subscribe({
        next: () => {
          this.loadLeaseDocuments();
        },
        error: err => {
          console.log(err);
        }
      });
    }
  }

}
